import {Component, EventEmitter, Input, Output, signal} from '@angular/core';
import {NotificationHubService} from "../../core/services";
import {NotificationMessage} from "../../common/model/WK.Ladon.Cms.Read.Queries.Notification.Model.NotificationMessage";
import {GroupAction, GroupActionPipe} from "../../ladon-api-enums/WK.Ladon.Cms.Common.Model.Enum.GroupAction";
import {Moment} from "moment";
import {NotificationType} from "../../ladon-api-enums/WK.Ladon.Cms.Common.Model.Enum.Notification.Type";

@Component({
    selector: 'ldn-notification-hub',
    templateUrl: './notification-hub.component.html',
    styleUrls: ['./notification-hub.component.scss'],
    standalone: false
})
export class NotificationHubComponent {
    public notifications = signal<INotification[]>([]);
    public lastNotificationId: number | null = null;
    public messageType = NotificationType;
    public readonly filterOptions = Array.from(GroupActionPipe.enumDescriptionMap.entries())
        .sort((a, b) => a[1].localeCompare(b[1]));

    private _visible: boolean = false;
    get visible(): boolean {
        return this._visible;
    }

    @Input()
    set visible(value: boolean) {
        if (value && !this._visible) {
            this.load();
        }
        this._visible = value;
        this.visibleChange.emit(value);

        if (!value) {
            this.handleHide();
        }
    }

    @Output() visibleChange = new EventEmitter<boolean>();
    public selectedFilter = signal<number>(null);
    private scrollElement: Element | null = null;
    private notReadNotificationIds: number[] = [];
    protected readonly GroupActionPipe = GroupActionPipe;

    constructor(private readonly notificationHubService: NotificationHubService) {
    }

    public load(olderThenNotificationId: number = null): void {
        this.notificationHubService.get(olderThenNotificationId, this.selectedFilter()).subscribe(x => {
            const notifications = this.groupMessages(x);
            this.lastNotificationId = notifications[notifications.length - 1]?.id;
            if (olderThenNotificationId !== null) {
                this.notifications.update((x) => {
                    x.push(...notifications);
                    return x;
                });
            } else {
                this.notifications.set(notifications);
            }
            this.notReadNotificationIds.push(...notifications.filter(x => !x.read).map(x => x.id));
        });
    }

    public loadMore(): void {
        if (this.lastNotificationId) {
            this.load(this.lastNotificationId);
        }
    }

    public handleHide(): void {
        this.markAsRead();
        this.unbindScrollElement()
        this.scrollElement = null;
    }

    public bindScrollElement(): void {
        if (!this.scrollElement) {
            setTimeout(() => {
                const elements = document.getElementsByClassName('p-drawer-content');
                if (elements.length > 0) {
                    this.scrollElement = elements[0];
                    this.scrollElement.addEventListener('scrollend', this.scrollEvent.bind(this));
                }
            });
        }
    }

    private scrollEvent(e: Event) {
        if (this.scrollElement.scrollTop + this.scrollElement.clientHeight === this.scrollElement.scrollHeight) {
            this.loadMore();
        }
    }

    private unbindScrollElement(): void {
        if (this.scrollElement) {
            this.scrollElement.removeEventListener('scrollend', this.scrollEvent);
        }
    }

    public filter(action: [GroupAction, string]) {
        this.selectedFilter.set(action === null ? null : action[0]);
        this.lastNotificationId = null;
        this.load();
    }

    private groupMessages(messages: NotificationMessage[]): INotification[] {
        const notifications: INotification[] = [];
        // najpierw najnowsze
        for (const message of messages.sort((a, b) => a.messageId > b.messageId ? -1 : 1)) {
            // sprawdzamy czy już jest notyfikacja dla danej wiadomości
            let notification: INotification = notifications.find(x => x.id === message.notificationId);
            if (!notification) {
                // dla akcji zbiorczych opis z enuma
                let description = GroupActionPipe.enumDescriptionMap.get(message.groupAction);
                // dla pojedynczych notyfikacji od razu wiadomość
                if (message.expectedResults === 1 && message.content !== null) { // napdisanie tylko jak niepusta wiadomość
                    description = message.content;
                }
                notification = {
                    id: message.notificationId,
                    description: description,
                    expectedResults: message.expectedResults,
                    messages: [],
                    showMessages: false,
                    date: null,
                    worstType: NotificationType.Info,
                    read: true
                };
                if (message.messageId) {
                    notification.messages.push(message);
                    notification.date = message.addedDate;
                    notification.worstType = message.type;
                    notification.read = message.read;
                }
                notifications.push(notification);
            } else {
                // jak kolejna wiadomość do tej samej notyfikacji to ustalamy najgorszy typ i flagę read
                if (message.type > notification.worstType) {
                    notification.worstType = message.type;
                }
                if (!message.read) {
                    notification.read = false;
                }
                notification.messages.push(message);
                notification.expectedResults = Math.max(notification.expectedResults, notification.messages.length);
            }
        }
        // najpierw najnowsze
        return notifications.sort((a, b) => a.id > b.id ? -1 : 1);
    }

    private markAsRead(): void {
        const uniqueIds = this.notReadNotificationIds.filter((value, index, array) => array.indexOf(value) === index);
        this.notificationHubService.markReadNotifications(uniqueIds);
    }
}

export interface INotification {
    id: number;
    description: string;
    expectedResults: number;
    messages: NotificationMessage[];
    showMessages: boolean;
    date: Moment;
    worstType: NotificationType;
    read: boolean;
}